// @ts-nocheck

import { uniq, head, isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { BoostedSportsByCategories } from '../../../constants/boostedOdds';
import { __ } from '../../../i18n/l10n';
import { SportIds } from '../../../types/sports';

import { getBetslipFromProps } from './common';

const getHistoryOutcomes = (state) => state.history.entities.outcomes;
const getHistoryMatches = (state) => state.history.entities.matches;
const getHistoryBets = (state) => state.history.entities.bets;
const getHistoryTournaments = (state) => state.history.entities.tournaments;
const getHistoryCategories = (state) => state.history.entities.categories;
const selectOutcomeIdFromProps = (_, { outcomeId }) => outcomeId;

const getEntitiesOutcomes = (state) => state.entities.outcomes;
const getEntitiesMatches = (state) => state.entities.matches;
const getEntitiesBets = (state) => state.entities.bets;
const getEntitiesTournaments = (state) => state.entities.tournaments;
const getEntitiesCategories = (state) => state.entities.categories;

const selectOutcomes = createSelector(
  [getHistoryOutcomes, getEntitiesOutcomes],
  (historyOutcomes, entitiesOutcomes) => {
    if (!isEmpty(historyOutcomes)) {
      return historyOutcomes;
    }

    return entitiesOutcomes;
  },
);

const selectMatches = createSelector([getHistoryMatches, getEntitiesMatches], (historyMatches, entitiesMatches) => {
  if (!isEmpty(historyMatches)) {
    return historyMatches;
  }

  return entitiesMatches;
});

const selectBets = createSelector([getHistoryBets, getEntitiesBets], (historyBets, entitiesBets) => {
  if (!isEmpty(historyBets)) {
    return historyBets;
  }

  return entitiesBets;
});

const selectTournaments = createSelector(
  [getHistoryTournaments, getEntitiesTournaments],
  (historyTournaments, entitiesTournaments) => {
    if (!isEmpty(historyTournaments)) {
      return historyTournaments;
    }

    return entitiesTournaments;
  },
);

const selectCategories = createSelector(
  [getHistoryCategories, getEntitiesCategories],
  (historyCategories, entitiesCategories) => {
    if (!isEmpty(historyCategories)) {
      return historyCategories;
    }

    return entitiesCategories;
  },
);

export const selectOutcome = createSelector(
  [selectOutcomes, selectOutcomeIdFromProps],
  (outcomes, outcomeId) => outcomes?.[outcomeId],
);

export const selectSubOutcomes = createSelector([selectOutcome], (outcome) => outcome?.subOutcomes);

export const selectBetBuilderSubOutcomes = createSelector(
  [selectBets, selectOutcomes, selectSubOutcomes],
  (bets, outcomes, subOutcomes) => {
    if (subOutcomes) {
      return (
        subOutcomes?.map((outcomeId) => {
          if (Array.isArray(outcomeId)) {
            const itemOutcomes = outcomeId.map((id) => outcomes[id]);
            const betIds = itemOutcomes.map((outcome) => outcome.betId);
            const uniqBetIds = uniq(betIds);
            const uniqBetId = head(uniqBetIds);
            const bet = bets[uniqBetId];
            const { betTitle } = bet;
            return {
              outcomeId,
              outcomeLabel: itemOutcomes.map((outcome) => outcome.label).join(` ${__('or')} `),
              betTitle,
              outcomeHasOccured: itemOutcomes.reduce((acc, cur) => acc || cur.hasOccured, false),
              isCancelled: itemOutcomes.reduce((acc, cur) => acc && cur.isCancelled, true),
            };
          }
          const { label, betId, hasOccured, isCancelled } = outcomes[outcomeId];
          const { betTitle, isCancelled: isBetCancelled } = bets[betId];
          return {
            outcomeId,
            outcomeLabel: label,
            betTitle,
            outcomeHasOccured: hasOccured,
            isCancelled: isCancelled || isBetCancelled,
          };
        }) ?? []
      );
    }
    return null;
  },
);

export const getOutcomeIdFromProps = (_, { outcomeId }) => outcomeId;

export const selectIsBanker = createSelector([getBetslipFromProps, getOutcomeIdFromProps], (betslip, outcomeId) =>
  betslip?.bankers ? betslip.bankers.indexOf(outcomeId) !== -1 : false,
);

export const selectBet = createSelector([selectOutcome, selectBets], (outcome, bets) => bets[outcome?.betId]);

export const selectBetTitle = createSelector([selectBet], (bet) => bet?.betTitle);

export const selectBetIdFromOutcome = createSelector([selectOutcome], (outcome) => outcome.betId);

export const selectMatchIdFromBet = createSelector(
  [selectBets, selectBetIdFromOutcome],
  (bets, betId) => bets[betId]?.matchId,
);

export const selectMatchFromOutcomeId = createSelector(
  [selectOutcome, selectBets, selectMatches],
  (outcome, bets, matches) => {
    const { betId } = outcome ?? {};
    const { matchId } = bets?.[betId] ?? {};
    return matches?.[matchId];
  },
);

export const selectMatchStart = createSelector([selectMatchFromOutcomeId], (match) =>
  !match?.isOutright ? match?.matchStart : null,
);

export const selectMatchTitle = createSelector([selectMatchFromOutcomeId], (match) => match?.title);

export const selectSportId = createSelector(
  [selectOutcome, selectBets, selectMatches, selectTournaments, selectCategories],
  (outcome, bets, matches, tournaments, categories) => {
    const { betId } = outcome ?? {};
    const { matchId } = bets?.[betId] ?? {};
    const { sportId: matchSportId, tournamentId } = matches?.[matchId] ?? {};
    const { categoryId } = tournaments?.[tournamentId] ?? {};
    const { sportId: categorySportId } = categories?.[categoryId] ?? {};

    const selectedSportId = matchSportId ?? categorySportId;
    return selectedSportId === SportIds.SPECIAL_ODDS ? BoostedSportsByCategories[categoryId] : selectedSportId;
  },
);

export const selectOutcomeHasOccured = createSelector([selectOutcome], (outcome) => outcome?.hasOccured);

export const selectOutcomeLabel = createSelector([selectOutcome], (outcome) => outcome?.label);

export const selectIsOutcomeCancelled = createSelector([selectOutcome], (outcome) => !!outcome?.isCancelled);

export const selectBetShareOutcome = createSelector(
  [
    selectMatchTitle,
    selectMatchStart,
    selectSportId,
    selectOutcomeHasOccured,
    selectOutcomeLabel,
    selectIsOutcomeCancelled,
    selectBetTitle,
    selectBetBuilderSubOutcomes,
    selectIsBanker,
  ],
  (matchTitle, matchStart, sportId, outcomeHasOccured, outcomeLabel, isCancelled, betTitle, subOutcomes, isBanker) => ({
    outcomeLabel,
    outcomeHasOccured,
    betTitle,
    matchTitle,
    matchStart,
    isCancelled,
    sportId,
    isBetbuilder: !!subOutcomes,
    isBanker,
    subOutcomes,
  }),
);
